import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle
} from 'react';
import {
  Card,
  CardBody,
  Row,
  Col,
  Label,
  Button,
  Alert,
  Collapse
} from 'reactstrap';
import { useToasts } from 'react-toast-notifications';

const defaultTitular = {
  cliIdentificacion: '',
  nombreCompleto: '',
  direccion: '',
  celular: '',
  email: ''
};

const FormularioTitulares = forwardRef(({ values, planIndividual, setLoading }, ref) => {
  const { addToast } = useToasts();
  const [titulares, setTitulares] = useState([{ ...defaultTitular }]);
  const [errors, setErrors] = useState([]);
  const [showAlert, setShowAlert] = useState(false);
  const [collapseStates, setCollapseStates] = useState([true]);

  useEffect(() => {
    if (values && Array.isArray(values) && values.length > 0) {
      // Normalizar para asegurar que cada titular tenga email/celular
      const norm = values.map(v => ({ ...defaultTitular, ...v }));
      setTitulares(norm);
      setErrors(norm.map(() => ({})));
      setCollapseStates(norm.map((_, i) => i === norm.length - 1));
    }
  }, [values]);

  const handleChange = async (index, field, value) => {
    const updated = [...titulares];
    updated[index][field] = value;
    setTitulares([...updated]);

    // Lógica de Autocompletado si el campo cambiado fue la Identificación
    if (field === 'cliIdentificacion') {
      const identificacion = value.trim();
      let tipo = "";

      if (identificacion.length === 10) {
        tipo = "cedula";
      } else if (identificacion.length === 13) {
        tipo = "ruc";
      }

      if (tipo !== "") {
        if (setLoading) setLoading(true);
        try {
          const { consultarDatosIdentificacion } = await import("./utils/ContratanteUtils");
          const data = await consultarDatosIdentificacion(identificacion, tipo);

          if (data) {
            const updatedAfterFetch = [...titulares];

            updatedAfterFetch[index] = {
              ...updatedAfterFetch[index],
              nombreCompleto: tipo === "cedula" ? (data.nombre || (data.nombres + " " + data.apellidos)) : data.nombre,
              direccion: data.direccion || updatedAfterFetch[index].direccion || "",
              email: data.correo || updatedAfterFetch[index].email || "",
              celular: data.celular || updatedAfterFetch[index].celular || ""
            };

            setTitulares(updatedAfterFetch);
            addToast("Datos cargados correctamente", { appearance: "success" });
          }
        } catch (error) {
          console.error("Error al consultar la identificación en pagadores:", error);
          // Opcionalmente mostrar alerta en caso de fallar consulta
        } finally {
          if (setLoading) setLoading(false);
        }
      }
    }
  };

  const agregarTitular = () => {
    setTitulares(prev => [...prev, { ...defaultTitular }]);
    setErrors(prev => [...prev, {}]);
    setCollapseStates(prev => prev.map(() => false).concat(true));
  };

  const eliminarTitular = (index) => {
    const updated = [...titulares];
    updated.splice(index, 1);
    setTitulares(updated);

    const updatedErrors = [...errors];
    updatedErrors.splice(index, 1);
    setErrors(updatedErrors);

    let updatedCollapse = [...collapseStates];
    updatedCollapse.splice(index, 1);
    updatedCollapse = updatedCollapse.map(() => false);
    if (updatedCollapse.length > 0) {
      updatedCollapse[updatedCollapse.length - 1] = true;
    }
    setCollapseStates(updatedCollapse);
  };

  const toggleCollapse = (index) => {
    setCollapseStates(prev => prev.map((val, i) => (i === index ? !val : val)));
  };

  const validar = () => {
    let valid = true;
    const newErrors = [];

    titulares.forEach((tit, i) => {
      const err = {};
      if (!tit.cliIdentificacion || !/^[0-9]{10,13}$/.test(String(tit.cliIdentificacion))) {
        err.cliIdentificacion = 'Identificación inválida';
      }
      if (!tit.nombreCompleto || tit.nombreCompleto.trim().length === 0) {
        err.nombreCompleto = 'Campo requerido';
      } else if (tit.nombreCompleto.trim().length < 3) {
        err.nombreCompleto = 'Ingrese un nombre válido';
      }
      if (!tit.direccion) err.direccion = 'Campo requerido';

      // Celular: 10 dígitos
      if (!tit.celular || !/^\d{10}$/.test(String(tit.celular))) {
        err.celular = 'Celular inválido (10 dígitos)';
      }

      // Email simple
      if (!tit.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(tit.email))) {
        err.email = 'Email inválido';
      }

      if (Object.keys(err).length > 0) valid = false;
      newErrors[i] = err;
    });

    setErrors(newErrors);
    setShowAlert(!valid);
    return valid;
  };

  useImperativeHandle(ref, () => ({
    obtenerDatos: () => {
      if (validar()) {
        return titulares;
      } else {
        return null;
      }
    },
    getTitulares: () => {
      return titulares;
    }
  }));

  return (
    <div>
      <Col md="12" className="text-end mt-3">
        {showAlert && (
          <Alert color="danger" toggle={() => setShowAlert(false)}>
            Hay errores en el formulario. Revisa los campos marcados en rojo.
          </Alert>
        )}
      </Col>

      {titulares.map((tit, index) => (
        <Card key={index} className="mb-3">
          <CardBody>
            <Row className="align-items-center">
              <Col md="11">
                <h5 style={{ marginBottom: 0 }}>
                  Pagador {index + 1}: {tit.nombreCompleto}
                </h5>
              </Col>
              <Col md="1" className="text-end">
                <Button
                  style={{
                    backgroundColor: '#001871',
                    color: '#fff',
                    border: 'none',
                    width: '35px',
                    height: '35px',
                    padding: '0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%'
                  }}
                  onClick={() => toggleCollapse(index)}
                >
                  <i className={`fa ${collapseStates[index] ? 'fa-chevron-up' : 'fa-chevron-down'}`} />
                </Button>
              </Col>
            </Row>
            <Collapse isOpen={collapseStates[index]}>
              <div className="mt-3">
                <Row>
                  <Col md="6">
                    <Label>Número de Identificación</Label>
                    <input
                      type="text"
                      className="form-control"
                      value={tit.cliIdentificacion}
                      onChange={(e) => handleChange(index, 'cliIdentificacion', e.target.value)}
                    />
                    {errors[index] && errors[index].cliIdentificacion ? (
                      <small style={{ color: 'red' }}>{errors[index].cliIdentificacion}</small>
                    ) : null}
                  </Col>
                  <Col md="6">
                    <Label>Nombre Completo</Label>
                    <input
                      type="text"
                      className="form-control"
                      maxLength={45}
                      value={tit.nombreCompleto}
                      onChange={(e) => handleChange(index, 'nombreCompleto', e.target.value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ\s]/g, ''))}
                    />
                    {errors[index] && errors[index].nombreCompleto ? (
                      <small style={{ color: 'red' }}>{errors[index].nombreCompleto}</small>
                    ) : null}
                  </Col>

                  <Col md="6" className="mt-3">
                    <Label>Celular</Label>
                    <input
                      type="tel"
                      className="form-control"
                      maxLength={10}
                      value={tit.celular}
                      onChange={(e) =>
                        handleChange(index, 'celular', e.target.value.replace(/\D/g, ''))
                      }
                      placeholder="Ej: 0999999999"
                    />
                    {errors[index] && errors[index].celular ? (
                      <small style={{ color: 'red' }}>{errors[index].celular}</small>
                    ) : null}
                  </Col>

                  <Col md="6" className="mt-3">
                    <Label>Email</Label>
                    <input
                      type="email"
                      className="form-control"
                      value={tit.email}
                      onChange={(e) => handleChange(index, 'email', e.target.value)}
                      placeholder="ejemplo@dominio.com"
                    />
                    {errors[index] && errors[index].email ? (
                      <small style={{ color: 'red' }}>{errors[index].email}</small>
                    ) : null}
                  </Col>

                  <Col md="12" className="mt-3">
                    <Label>Dirección</Label>
                    <input
                      type="text"
                      className="form-control"
                      value={tit.direccion}
                      onChange={(e) => handleChange(index, 'direccion', e.target.value)}
                    />
                    {errors[index] && errors[index].direccion ? (
                      <small style={{ color: 'red' }}>{errors[index].direccion}</small>
                    ) : null}
                  </Col>

                  <Col md="12" className="text-end mt-3">
                    {titulares.length > 1 && (
                      <Button color="danger" onClick={() => eliminarTitular(index)}>
                        Eliminar pagador
                      </Button>
                    )}
                  </Col>
                </Row>
              </div>
            </Collapse>
          </CardBody>
        </Card>
      ))}

      <div className="text-end mb-3 flex" style={{ flexDirection: 'row', justifyContent: 'end' }}>
        {!planIndividual && (
          <Button
            style={{
              marginLeft: 10,
              backgroundColor: '#001871',
              color: 'white',
              border: 'none'
            }}
            onClick={agregarTitular}
          >
            + Agregar otro pagador
          </Button>
        )}
      </div>
    </div>
  );
});

export default FormularioTitulares;
